/**
 * Classifies the current browser and device into normalized buckets for the
 * user-tech analytics report (Device + Browser breakdowns). Unlike the detailed,
 * human-readable summary the support form builds, this returns structured values
 * suitable for storing on the user document and charting (Windows, Android, iPad,
 * Chrome, Safari, etc.).
 */

export interface DeviceBrowserInfo {
  deviceType: string;
  browser: string;
}

/**
 * Detect the operating-system / device family from the user agent.
 * Buckets: Windows, macOS, iPad, iPhone, Android, Chrome OS, Linux, Unknown.
 */
export const getDeviceType = (): string => {
  if (typeof navigator === 'undefined') return 'Unknown';

  const ua = navigator.userAgent;

  // iPadOS 13+ reports a Mac-like UA but exposes multi-touch — check it first.
  if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) {
    return 'iPad';
  }
  if (/iPhone|iPod/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) return 'Android';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
  if (/CrOS/i.test(ua)) return 'Chrome OS';
  if (/Linux/i.test(ua)) return 'Linux';

  return 'Unknown';
};

/**
 * Detect the browser family from the user agent.
 * Buckets: Edge, Opera, Firefox, Chrome, Safari, Other.
 * Order matters: Edge and Opera both carry "Chrome"/"Safari" tokens, and Chrome
 * carries a "Safari" token — so the more specific checks run first.
 */
export const getBrowserName = (): string => {
  if (typeof navigator === 'undefined') return 'Other';

  const ua = navigator.userAgent;

  if (/Edg\//i.test(ua)) return 'Edge';
  if (/OPR\/|Opera/i.test(ua)) return 'Opera';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) return 'Chrome';
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'Safari';

  return 'Other';
};

/**
 * Combined normalized device + browser classification for analytics.
 */
export const getDeviceBrowserInfo = (): DeviceBrowserInfo => ({
  deviceType: getDeviceType(),
  browser: getBrowserName(),
});
